import { CateringDestination } from '@bufteam/cfacorp_d2c.bufbuild_es/cfa/d2c/timeslots/v1/timeslots_service_pb';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { leaveBreadcrumb, notifyBugsnag } from 'cfa-react-logging';

import ooeConstants from '../constants';
import { isDateBeforeRestaurantToday } from '../eventDetails/utils';
import {
  selectCmtCustomerCFAId,
  selectCmtCustomerIsRegisteredMember,
  selectEmail,
  selectFirstName,
  selectFullName,
  selectLastName,
  selectPhoneFormForApi,
} from '../guest/selectors';
import type { State } from '../reducers';
import { logEvent } from '../services/analytics';
import { deleteOrderReport } from '../shared/apis';
import { d2cRestaurantClient, d2cTimeslotsClient, rexCateringClient } from '../shared/clients';
import {
  selectEditNeedsCancellation,
  selectNeedsProfilePatchCall,
  selectOrderForAPI,
  selectOrderWithPayment,
} from '../shared/selectors';
import { DuplicateOrderErrorPayload, EditOrderErrorPayload } from '../types/order';
import { selectLocationNumber, selectLocationTimezone } from '../user/selectors';
import { logDatadogError } from '../services/datadog';
import { submitWarningErrorMessages } from '../util/customerErrorMessages';
import {
  cancelOrder,
  getCateringDeliveryLocationsFromProtobuf,
  initiateOrder,
  submitOrderApi,
  requestPaymentApi,
  resubmitOrder,
  validateAddressFromProtobuf as validateAddressFromProtobufApi,
} from './apis';
import {
  AddressResult,
  AvailableDates,
  availableDatesSchema,
  Order,
  ValidatedDeliveryAddress,
} from './schemas';
import {
  selectDeliveryAddress,
  selectDeliveryAddressForOrder,
  selectDeliveryAddressPlaceId,
  selectDestination,
  selectDetailsDate,
  selectEditOrderId,
  selectEditOrderOriginalCustomerCFAId,
  selectNewPaymentMethod,
  selectOrder,
} from './selectors';
import { autoCompleteToAddressResult } from './transforms';

export const submitOrder = createAsyncThunk('order/submitOrder', async (_, thunkAPI) => {
  const { getState, rejectWithValue } = thunkAPI;
  const state = getState() as State;

  const newPaymentMethod = selectNewPaymentMethod(state);
  const needsProfilePatchCall = selectNeedsProfilePatchCall(state);
  const orderWithPayment = selectOrderWithPayment(state);
  const editOrderId = selectEditOrderId(state);
  const cfaId = selectCmtCustomerCFAId(state);
  const isRegisteredMember = selectCmtCustomerIsRegisteredMember(state);
  const detailsDate = selectDetailsDate(state);
  const timeZone = selectLocationTimezone(state);

  if (orderWithPayment.id === undefined) {
    throw new Error('Order ID is required to submit order');
  }

  // AC6: always re-read wall-clock today so midnight cannot slip past memoized selectors.
  if (editOrderId && detailsDate && isDateBeforeRestaurantToday(detailsDate, timeZone)) {
    throw new Error(submitWarningErrorMessages.PROMISE_DATE_PASSED);
  }

  if (orderWithPayment.destination === ooeConstants.DELIVERY) {
    const apiDeliveryAddress = orderWithPayment.deliveryAddress;
    const selectorDeliveryAddress = selectDeliveryAddressForOrder(state);
    const { addressLine1, city, state: addressState, zip } = apiDeliveryAddress ?? {};
    if (!addressLine1 || !city || !addressState || !zip) {
      notifyBugsnag({
        grouping: 'Empty delivery address fields detected during order submission',
        message: 'Submit Order - Empty Delivery Address',
        error: {
          apiPayloadDeliveryAddress: apiDeliveryAddress,
          selectorDeliveryAddress,
          editOrderId,
          orderId: orderWithPayment.id,
        },
      });
    }
  }

  leaveBreadcrumb('Submit Order', {
    message: 'In SubmitOrder',
    newPaymentMethod,
    needsProfilePatchCall,
    orderWithPayment,
    editOrderId,
  });

  if (needsProfilePatchCall) {
    const firstName = selectFirstName(state);
    const lastName = selectLastName(state);
    const email = selectEmail(state);
    const phone = selectPhoneFormForApi(state);

    await rexCateringClient.updateProfile({
      ...(firstName && { firstName }),
      ...(lastName && { lastName }),
      ...(email && { contactEmail: email }),
      ...(phone && { contactPhone: phone }),
      cfaId,
      isRegisteredMember,
    });
  }

  let submittedOrder: Order;
  if (editOrderId) {
    const orderNeedsCancellation = selectEditNeedsCancellation(state);

    if (orderNeedsCancellation) {
      const newOrder = await initiateOrder({
        order: { ...selectOrderForAPI(state), id: undefined },
        cfaId,
        isRegisteredMember,
      });
      submittedOrder = await submitOrderApi({
        order: { ...orderWithPayment, id: newOrder.id },
        cfaId,
        isRegisteredMember,
      });
      try {
        const locationNumber = selectLocationNumber(state);
        const editCfaId = selectEditOrderOriginalCustomerCFAId(state);
        await deleteOrderReport({ orderId: editOrderId, locationNumber, cfaId: editCfaId });
        logEvent('Edit Order Cancellation', {
          message: 'Successfully cancelled original order after editing',
          originalOrderId: editOrderId,
          newOrderId: submittedOrder.id,
        });
      } catch (error) {
        try {
          await cancelOrder({ orderId: submittedOrder.id, cfaId, isRegisteredMember });
          leaveBreadcrumb('Edit Guest Details Cancel New Order', {
            message: 'Successfully cancelled newly created order, original order still exists',
            originalOrderId: editOrderId,
            cancelledNewOrderId: submittedOrder.id,
            userImpact: 'User will see error screen to retry editing',
          });

          notifyBugsnag({
            grouping: 'Failed to cancel original order during guest edit flow.',
            message: 'Submit Order - Cancel Original Order Failed',
            error: {
              originalOrderId: editOrderId,
              cancelledNewOrderId: submittedOrder.id,
              originalCancelError: error,
              userImpact: 'Guest will see EditOrderError screen prompting them to try again',
              resolution: 'New order cancelled successfully, no duplicate charge risk',
              currentState: 'Original order remains active, guest must retry their edit',
            },
          });

          logDatadogError('Submit Order - Cancel Original Order Failed', {
            originalOrderId: editOrderId,
            cancelledNewOrderId: submittedOrder.id,
            originalCancelError: error,
          });

          return rejectWithValue({
            errorType: ooeConstants.ORIGINAL_ORDER_CANCEL_FAILED,
            originalOrderId: editOrderId,
          } as EditOrderErrorPayload);
        } catch (rollbackError) {
          notifyBugsnag({
            grouping: 'Failed to cancel both original and newly created orders during guest edit flow.',
            message: 'Submit Order - Duplicate Orders Created',
            error: {
              originalOrderId: editOrderId,
              newOrderDetails: {
                id: submittedOrder.id,
                userId: submittedOrder.user?.id,
                transactionId: submittedOrder.transactionId,
              },
              firstCancelError: error,
              secondCancelError: rollbackError,
            },
          });
          logDatadogError('Submit Order - Duplicate Orders Created', {
            originalOrderId: editOrderId,
            newOrderDetails: {
              id: submittedOrder.id,
              userId: submittedOrder.user?.id,
              transactionId: submittedOrder.transactionId,
            },
            firstCancelError: error,
            secondCancelError: rollbackError,
          });
          const guestName = selectFullName(state);
          return rejectWithValue({
            errorType: ooeConstants.ORIGINAL_AND_EDITED_ORDER_CANCEL_FAILED,
            originalOrderId: editOrderId,
            order: submittedOrder,
            guestName,
          } as DuplicateOrderErrorPayload);
        }
      }
    } else if (newPaymentMethod) {
      submittedOrder = await submitOrderApi({ order: orderWithPayment, cfaId, isRegisteredMember });
    } else {
      submittedOrder = await resubmitOrder({ order: orderWithPayment, cfaId, isRegisteredMember });
    }
  } else {
    submittedOrder = await submitOrderApi({ order: orderWithPayment, cfaId, isRegisteredMember });
  }

  if (newPaymentMethod) {
    await requestPaymentApi({ orderId: submittedOrder.id, cfaId, isRegisteredMember });
  }
  const orderState = selectOrder(state);

  return { previousOrderState: orderState, order: submittedOrder };
});

export const updateOrder = createAsyncThunk('order/updateOrder', async (_, thunkAPI): Promise<Order> => {
  const { getState } = thunkAPI;
  const state = getState() as State;

  const order = selectOrderForAPI(state);
  const cfaId = selectCmtCustomerCFAId(state);
  const isRegisteredMember = selectCmtCustomerIsRegisteredMember(state);

  let orderToSend = order;

  // TODO: talk to DXE about ability pass previous order ID so we can chain all order updates
  // Always set order id to undefined
  orderToSend = { ...orderToSend, id: undefined };

  leaveBreadcrumb('Update Pricing', {
    message: 'Update pricing via initiateOrder',
    orderToSend,
  });

  const updatedOrder = await initiateOrder({ order: orderToSend, cfaId, isRegisteredMember });
  return updatedOrder;
});

export const autocompleteAddress = createAsyncThunk(
  'order/autocompleteAddress',
  async (payload: { address: string }): Promise<{ address: string; results: AddressResult[] }> => {
    const { address } = payload;
    const { autocompleteResults } = await d2cRestaurantClient.getAutoCompleteResult({
      partialAddress: address,
    });
    const results = autocompleteResults.map((r) => autoCompleteToAddressResult.parse(r));
    return { address, results };
  },
);

export const getOrderDates = createAsyncThunk(
  'order/getOrderDates',
  async (payload: { orderId: string }, thunkAPI): Promise<AvailableDates> => {
    const { getState } = thunkAPI;
    const state = getState() as State;
    const locationNumber = selectLocationNumber(state);
    const destination = selectDestination(state);
    const { orderId } = payload;

    leaveBreadcrumb('Get Order Dates', {
      message: 'Calling getAvailableDates',
      orderId,
    });

    const results = await d2cTimeslotsClient.getAvailableDates({
      destination:
        destination === ooeConstants.DELIVERY ? CateringDestination.DELIVERY : CateringDestination.PICKUP,
      locationNumbers: [locationNumber],
    });
    return availableDatesSchema.parse(results);
  },
);

export const validateDeliveryRange = createAsyncThunk('order/validateDeliveryRange', async (_, thunkAPI) => {
  const { getState } = thunkAPI;
  const state = getState() as State;
  const locationNumber = selectLocationNumber(state);
  const deliveryAddress = selectDeliveryAddress(state);
  const placeId = selectDeliveryAddressPlaceId(state);

  leaveBreadcrumb('Validate Delivery Range', {
    message: 'Calling validateDeliveryRange',
    deliveryRangeAddress: deliveryAddress,
    selectedLocation: locationNumber,
  });

  const deliveryLocations = await getCateringDeliveryLocationsFromProtobuf({ placeId: placeId ?? '' });

  const cateringLocations = deliveryLocations.filter(
    (location) => location.mobileOrderingSettings?.fulfillmentMethods.cateringDelivery,
  );
  const availableLocations = cateringLocations.map((location) => ({
    locationNumber: location.locationNumber,
    name: location.name,
    locationContact: { daytimePhone: location.locationContact?.daytimePhone },
  }));
  const valid = deliveryLocations.map((location) => location.locationNumber).includes(locationNumber);
  return { valid, availableLocations };
});

export const validateAddress = createAsyncThunk<
  { address: string; response: ValidatedDeliveryAddress },
  { address: string; autocompleteResult: AddressResult; apartment?: string },
  {
    rejectValue: {
      address: string;
    };
  }
>(
  'order/validateAddress',
  async (
    payload: {
      address: string;
      autocompleteResult: AddressResult;
      apartment?: string;
    },
    thunkAPI,
  ) => {
    const { address, autocompleteResult } = payload;
    const { rejectWithValue } = thunkAPI;

    try {
      const response = await validateAddressFromProtobufApi(autocompleteResult);
      return { address, response };
    } catch (e) {
      leaveBreadcrumb('order/validateAddress thunk error', e);
      return rejectWithValue({ address });
    }
  },
);
